import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { updateProject } from "@/components/common/project-provider/update-project";
import { changeMode } from "@/store/mode-slice";
import {
  setWizardElementToAlignId,
  setWizardReferenceElementId,
} from "@/store/modes/alignment-wizard-mode-slice";
import { AreaImgSheetParameter } from "@/store/modes/import-sheet-mode-slice";
import { setActiveElement, setSheetVisibility } from "@/store/selections-slice";
import { store } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { FileUploadTask } from "@/utils/background-tasks";
import {
  computeReferenceSystemProperties,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectRootIElement,
} from "@faro-lotv/app-component-toolbox";
import { assert, getFileExtension, GUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementType,
  IElementTypeHint,
  isIElementGenericImgSheet,
  isIElementGroup,
  isIElementWithTypeAndHint,
  isValidPose,
  MAX_NAME_LENGTH,
} from "@faro-lotv/ielement-types";
import {
  createMutationAddArea,
  Mutation,
  mutationAddImgSheet,
  MutationAddImgSheet,
  mutationAddImgSheetTiled,
  MutationAddImgSheetTiled,
  SourcePdfInfo,
} from "@faro-lotv/service-wires";
import { useCallback } from "react";
import { DEFAULT_PDF_PAGE_TO_RENDER as DEFAULT_PDF_PAGE_SELECTED } from "./use-parse-user-image";

/** Maximum allowed image height in pixels */
export const MAX_ALLOWED_IMG_HEIGHT = 8192;

/** Maximum allowed image width in pixels */
export const MAX_ALLOWED_IMG_WIDTH = 8192;

/**
 * @param text string to check the validity of
 * @returns the validation error, or undefined if the string is valid
 */
export function verifyInputName(text?: string): string | undefined {
  if (!text) {
    return "Sheet name is required";
  }
  if (text.length > MAX_NAME_LENGTH) {
    return `Sheet name must not exceed ${MAX_NAME_LENGTH} characters`;
  }
}

/** All supported Image Sheet file extensions.  */
export enum SupportedImgSheetFileExtensions {
  pdf = "pdf",
  png = "png",
  jpeg = "jpeg",
  jpg = "jpg",
}

/**
 * List of supported Image Sheet file extensions without the dots.
 */
export const ImgSheetFileExtensionsList = Object.values(
  SupportedImgSheetFileExtensions,
).map((ext) => `${ext}`);

/**
 * List of supported Image Sheet file extensions with the dots.
 * The dot is required in order to work with the accept property of the file input tag.
 */
export const ImgSheetFileExtensionsWithDotsList = Object.values(
  SupportedImgSheetFileExtensions,
).map((ext) => `.${ext}`);

/**
 * @param fileName name of the file to check the extension of
 * @returns if the name passed has a supported image sheet extension
 */
export function isSupportedImgSheetFileExtension(fileName: string): boolean {
  const extension = getFileExtension(fileName)?.toLowerCase() ?? "";

  return ImgSheetFileExtensionsList.includes(extension);
}

/**
 * @param task to check
 * @returns true if the task is an upload background task of an image sheet file
 */
export function isImageFileUploadTask(task: FileUploadTask): boolean {
  return isSupportedImgSheetFileExtension(task.metadata.filename);
}

type CreateAreaImgSheetsFunction = (
  areaImgSheets: AreaImgSheetParameter[],
  existingArea: IElement | undefined,
  sourcePdfInfo?: SourcePdfInfoNoPageNumber,
) => Promise<void>;

/** Type representing source PDF information without the selected page number */
export type SourcePdfInfoNoPageNumber = Omit<SourcePdfInfo, "pageNumber">;

/**
 * @returns a function to create areas and image sheets
 */
export function useCreateAreaImgSheets(): CreateAreaImgSheetsFunction {
  const projectApi = useCurrentProjectApiClient();
  const dispatch = useAppDispatch();
  const root = useAppSelector(selectRootIElement);
  const slideContainer = useAppSelector(
    selectChildDepthFirst(root, (iElement: IElement) =>
      isIElementWithTypeAndHint(
        iElement,
        IElementType.group,
        IElementTypeHint.slideContainer,
      ),
    ),
  );

  /**
   * This hook will create image sheets (or tiled sheets) and areas if required
   *
   * @returns void
   * @param existingArea the existing area to which new sheets will be added. If undefined, new areas will be created for each sheet.
   * @param areaImgSheets mutation creation parameters
   */
  return useCallback(
    async (
      areaImgSheets: AreaImgSheetParameter[],
      existingArea: IElement | undefined,
      sourcePdfInfo?: SourcePdfInfoNoPageNumber,
    ): Promise<void> => {
      areaImgSheets.sort((sheet1, sheet2) =>
        sheet1.name.localeCompare(sheet2.name),
      );

      let updateId = "";
      let newSheetId = "";
      if (existingArea) {
        const mutations = createAddImgSheetMutations(
          areaImgSheets,
          existingArea,
          sourcePdfInfo,
        );
        updateId = existingArea.id;
        newSheetId = mutations[0].newElement.id;
        await projectApi.applyMutations(mutations);
      } else {
        assert(
          slideContainer,
          "A SlideContainer must be present in the project in order to add an Area",
        );
        const mutations = createAddAreaAndImgSheetMutations(
          areaImgSheets,
          slideContainer,
          sourcePdfInfo,
        );
        updateId = slideContainer.id;
        await projectApi.applyMutations(mutations);
      }

      // Fetch the changed sub-tree and update the local copy of the project
      await dispatch(
        updateProject({
          projectApi,
          iElementQuery: {
            ancestorIds: [updateId],
          },
        }),
      );

      if (!existingArea) {
        dispatch(setActiveElement(areaImgSheets[0].areaId));
      }

      if (existingArea && areaImgSheets.length === 1) {
        const sheets = selectChildrenDepthFirst(
          existingArea,
          isIElementGenericImgSheet,
        )(store.getState());

        const newSheet = sheets.find((sheet) => sheet.id === newSheetId);
        if (newSheet) {
          dispatch(
            setSheetVisibility({ sheetId: newSheetId, isVisible: true }),
          );
          const candidateReferenceSheet = sheets.find(
            (sh) => sh.id !== newSheetId && isValidPose(sh.pose),
          );

          if (candidateReferenceSheet) {
            dispatch(setWizardReferenceElementId(candidateReferenceSheet.id));
          }
          dispatch(setWizardElementToAlignId(newSheetId));
          dispatch(changeMode("alignWizard"));
        }
      } else {
        dispatch(changeMode("sheet"));
      }
    },
    [slideContainer, dispatch, projectApi],
  );
}

function createAddImgSheetMutation(
  areaImgSheet: AreaImgSheetParameter,
  rootId: GUID,
  groupId?: string,
  sourcePdfInfo?: SourcePdfInfoNoPageNumber,
): MutationAddImgSheet | MutationAddImgSheetTiled {
  const mutationPayload = {
    rootId,
    groupId,
    sectionId: areaImgSheet.areaId,
    name: areaImgSheet.name,
    createdAt: new Date().toISOString(),
    fileName: areaImgSheet.fileName,
    fileSize: areaImgSheet.fileSize,
    md5Hash: areaImgSheet.md5Hash,
    uri: areaImgSheet.downloadUrl,
    thumbnailUri: areaImgSheet.additionalUrl,
    pixelWidth: areaImgSheet.imageWidth,
    pixelHeight: areaImgSheet.imageHeight,
    refCoordSystemMatrix: computeReferenceSystemProperties(
      {
        type: areaImgSheet.elementType,
        typeHint: IElementTypeHint.area,
      },
      false,
    ),
    sourcePdf: sourcePdfInfo
      ? {
          ...sourcePdfInfo,
          pageNumber: areaImgSheet.selectedPdfPage ?? DEFAULT_PDF_PAGE_SELECTED,
        }
      : undefined,
  };
  return areaImgSheet.elementType === IElementType.imgSheet
    ? mutationAddImgSheet(mutationPayload)
    : {
        ...mutationAddImgSheetTiled(mutationPayload),
        lowResImage: areaImgSheet.associatedRegularImageSheetInfo,
      };
}

function createAddImgSheetMutations(
  areaImgSheets: AreaImgSheetParameter[],
  sectionArea: IElement,
  sourcePdfInfo?: SourcePdfInfoNoPageNumber,
): Array<MutationAddImgSheet | MutationAddImgSheetTiled> {
  const groupArea = selectChildDepthFirst(
    sectionArea,
    (el) => isIElementGroup(el) && el.typeHint === IElementTypeHint.area,
  )(store.getState());

  return areaImgSheets.map((areaImgSheet) =>
    createAddImgSheetMutation(
      areaImgSheet,
      sectionArea.rootId,
      groupArea?.id,
      sourcePdfInfo,
    ),
  );
}

function createAddAreaAndImgSheetMutations(
  areaImgSheets: AreaImgSheetParameter[],
  slideContainer: IElement,
  sourcePdfInfo?: SourcePdfInfoNoPageNumber,
): Mutation[] {
  return areaImgSheets
    .map((areaImgSheet) => [
      createMutationAddArea({
        id: areaImgSheet.areaId,
        rootId: slideContainer.rootId,
        groupId: slideContainer.id,
        name: areaImgSheet.name,
      }),
      createAddImgSheetMutation(
        areaImgSheet,
        slideContainer.rootId,
        undefined,
        sourcePdfInfo,
      ),
    ])
    .flat();
}
